import _ from 'lodash'
import * as firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/firestore'
import Pass, { Contact, DynamicProperties } from '@/modules/pass/domain/pass'
import { PASS_DOWNLOAD_APPLE_API, PASS_DOWNLOAD_GOOGLE_API } from '@/config'
import axios from 'axios'

class PassService {
  async downloadGoogleCard (passId: string) {
    const url = `${PASS_DOWNLOAD_GOOGLE_API}/${passId}`
    try {
      const response = await axios.get(url)
      return response.data
    } catch (error) {
      return null
    }
  }

  getDownloadAppleUrl (passId: string) {
    return `${PASS_DOWNLOAD_APPLE_API}/${passId}`
  }

  async getPassByName (urlName: string) {
    if (!urlName) {
      return null
    }

    const passes = await firebase.firestore().collection('passes').where('urlName', '==', urlName.toLocaleLowerCase())
    const querySnapshot = await passes.get()

    if (querySnapshot.size !== 1) {
      return null
    }

    const docRef = querySnapshot.docs[0].ref
    return this.transformPass(docRef)
  }

  async getPass () {
    const user = firebase.auth().currentUser

    if (!user) {
      return null
    }

    const docRef = await firebase.firestore().collection('passes').doc(user!.uid)
    return this.transformPass(docRef)
  }

  async save (pass: Pass, savePublished?: boolean) {
    const user = firebase.auth().currentUser

    if (!user) {
      return null
    }

    const docRef = await firebase.firestore().collection('passes').doc(user!.uid)

    const data = { ..._.omit(pass.draftProps, 'lastChangeMoment') }
    data.contacts = data.contacts.map(contact => ({ ...contact }))
    this.consolidate(data)
    await docRef.collection('data').doc('draft').set(_.pickBy(data))

    if (savePublished) {
      const data = { ..._.omit(pass.publishedProps, 'lastChangeMoment') }
      data.contacts = data.contacts.map(contact => ({ ...contact }))
      await docRef.collection('data').doc('published').set(_.pickBy(data))
    }
    return true
  }

  async publish (pass: Pass) {
    pass.publish()
    return this.save(pass, true)
  }

  private createDynamicProperties (data: any) {
    return new DynamicProperties(data!.lastChange,
      data!.notification,
      data!.tests,
      data!.cases,
      data!.recovered,
      data!.news,
      data!.openingHour,
      data!.warning,
      data!.info,
      data!.covid,
      (data.contacts || []).map((contact: any) => new Contact(contact!.type, contact!.value, contact!.name)))
  }

  private async transformPass (docRef: firebase.firestore.DocumentReference) {
    const passObj = await docRef.get()
    const draftObj = await docRef.collection('data').doc('draft').get()
    const publishedObj = await docRef.collection('data').doc('published').get()

    const passData = passObj.data()
    const draftData = draftObj.data()
    const publishedData = publishedObj.data()

    return new Pass(docRef.id,
      passData!.name,
      this.createDynamicProperties(draftData),
      this.createDynamicProperties(publishedData))
  }

  // remove invalid LSEP and PSEP characters
  private consolidate (props: any) {
    props.news = this.removeLsepPsep(props.news)
    props.detail = this.removeLsepPsep(props.detail)
    props.openingHour = this.removeLsepPsep(props.openingHour)
    props.warning = this.removeLsepPsep(props.warning)
    props.info = this.removeLsepPsep(props.info)
    props.covid = this.removeLsepPsep(props.covid)
    props.contacts.forEach((value: any) => {
      value.name = this.removeLsepPsep(value.name)
      value.value = this.removeLsepPsep(value.value)
    })
  }

  private removeLsepPsep (text?: string | null) {
    return (text || '').replace(/[\u2028,\u2029]/g, '')
  }
}

export default new PassService()
